import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useReducedMotion } from 'motion/react'
import { EVENT_NAMES } from 'services/Analytics/Analytics'
import { ReactComponent as CheckCircleIcon } from '../../../icons/assets/CheckCircle.svg'
import { ReactComponent as PriceTagIcon } from '../../../icons/assets/PriceTag.svg'
import PromoCodeDialog from '../../../components/PromoCodeDialog/PromoCodeDialog.client'
import Skeleton from 'components/Skeleton/Skeleton'
import { DiscountProgressBar } from './discountProgress/DiscountProgressBar'
import { useBagAnalytics } from '../hooks/useBagAnalytics'
import { useDiscountProgress } from '../hooks/useDiscountProgress'
import { usePromoItems } from '../hooks/usePromoItems'
import * as S from './PromoCodeSegment.styles'

function PromoCodeSegment() {
  const { t } = useTranslation()
  const [showPromoDialog, setShowPromoDialog] = useState(false)
  const promoItems = usePromoItems()
  const discountProgress = useDiscountProgress()
  const reduceMotion = useReducedMotion() ?? false
  const hasPromoCode = promoItems.some(
    (entry) => entry.type === 'discount' && entry.code !== null
  )
  const promoLinkLabel = t(
    hasPromoCode ? 'order.cart_change_promo' : 'order.cart_add_promo'
  )
  const { trackBagEvent } = useBagAnalytics()

  const onChangePromoClick = () => {
    trackBagEvent(EVENT_NAMES.CHANGE_PROMO_CODE_TAPPED)
    setShowPromoDialog(true)
  }

  return (
    <S.PromoSection>
      {promoItems.length === 0 ? (
        <S.PromoRow>
          <S.PromoLabel>{t('order.cart_volume_discount')}</S.PromoLabel>
          <S.AddPromoLink
            onClick={onChangePromoClick}
            data-testid="cart-promo-link"
          >
            {promoLinkLabel}
          </S.AddPromoLink>
        </S.PromoRow>
      ) : (
        <S.EntryList>
          {promoItems.map(({ id, code, appliance }, index) => (
            <div key={id}>
              <S.PromoRow>
                {code ? (
                  <S.PromoCodeBadge data-testid="cart-promo-badge">
                    <PriceTagIcon />
                    {code}
                  </S.PromoCodeBadge>
                ) : (
                  <S.PromoLabel>{t('order.cart_volume_discount')}</S.PromoLabel>
                )}
                {index === 0 && (
                  <S.AddPromoLink
                    onClick={onChangePromoClick}
                    data-testid="cart-promo-link"
                  >
                    {promoLinkLabel}
                  </S.AddPromoLink>
                )}
              </S.PromoRow>
              {appliance?.state === 'loading' && (
                <S.DiscountAppliedRow $isPromo={!!code}>
                  <Skeleton width={180} />
                </S.DiscountAppliedRow>
              )}
              {appliance?.state === 'applied' && (
                <S.DiscountAppliedRow
                  $isPromo={!!code}
                  data-testid="cart-discount-description"
                  data-discountstatus={appliance.state}
                >
                  <CheckCircleIcon />
                  {appliance.text}
                </S.DiscountAppliedRow>
              )}
              {appliance?.state === 'not_met' && (
                <>
                  <S.DiscountDescription
                    data-testid="cart-discount-description"
                    data-discountstatus={appliance.state}
                  >
                    {appliance.text}
                  </S.DiscountDescription>
                  {discountProgress && (
                    <S.DiscountProgressBarContainer>
                      <DiscountProgressBar
                        steps={discountProgress.steps}
                        progress={
                          discountProgress.allReached
                            ? 100
                            : discountProgress.progress
                        }
                        reduceMotion={reduceMotion}
                        variant="white"
                      />
                    </S.DiscountProgressBarContainer>
                  )}
                </>
              )}
            </div>
          ))}
        </S.EntryList>
      )}
      <PromoCodeDialog
        open={showPromoDialog}
        onDismiss={() => setShowPromoDialog(false)}
        onSuccess={() => {
          setShowPromoDialog(false)
        }}
        source="cart"
      />
    </S.PromoSection>
  )
}

export default PromoCodeSegment
